import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/hooks/useAuth";
import { SiteSettingsProvider } from "@/hooks/useSiteSettings";
import { DesignModeProvider } from "@/hooks/useDesignMode";
import { useNotifications } from "@/hooks/useNotifications";
import { useInstantRenewal } from "@/hooks/useInstantRenewal";
import { useAppBadge } from "@/hooks/useAppBadge";
import DesignModeToolbar from "./components/design/DesignModeToolbar";
import DesignModeOverlays from "./components/design/DesignModeOverlays";
import DesignModePanel from "./components/design/DesignModePanel";
import DesignContentSelector from "./components/design/DesignContentSelector";
import DesignDomPanel from "./components/design/DesignDomPanel";
import DesignDomOverridesApplier from "./components/design/DesignDomOverridesApplier";
import LayersPanel from "./components/design/LayersPanel";
import PwaUpdatePrompt from "./components/PwaUpdatePrompt";
import PushPermissionPrompt from "./components/PushPermissionPrompt";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Search from "./pages/Search";
import PublicProfile from "./pages/PublicProfile";
import Contact from "./pages/Contact";
import ContactConversation from "./pages/ContactConversation";
import LegalNotice from "./pages/LegalNotice";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import BabysitterFrance from "./pages/BabysitterFrance";
import CityLanding from "./pages/CityLanding";
import CategoryHub from "./pages/CategoryHub";
import TarifsBabySitting from "./pages/TarifsBabySitting";
import CommentCaMarcheParents from "./pages/CommentCaMarcheParents";
import CommentCaMarchePros from "./pages/CommentCaMarchePros";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import EmailVerified from "./pages/EmailVerified";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import ParentAccessGate from "./components/ParentAccessGate";
import MessagesAccessRouter from "./components/MessagesAccessRouter";
import SupportChatBubble from "./components/support/SupportChatBubble";
import ProAccountCompletionModal from "./components/ProAccountCompletionModal";

const Booking = lazy(() => import("./pages/Booking"));
const ProDashboard = lazy(() => import("./pages/ProDashboard"));
const ProReservations = lazy(() => import("./pages/ProReservations"));
const CareRequests = lazy(() => import("./pages/CareRequests"));
const Favorites = lazy(() => import("./pages/Favorites"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSeo = lazy(() => import("./pages/AdminSeo"));
const AdminSocialAgent = lazy(() => import("./pages/AdminSocialAgent"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const Historique = lazy(() => import("./pages/Historique"));
const MesGains = lazy(() => import("./pages/MesGains"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Install = lazy(() => import("./pages/Install"));
const Notifications = lazy(() => import("./pages/Notifications"));
const AbonnementSucces = lazy(() => import("./pages/AbonnementSucces"));
const AbonnementAnnule = lazy(() => import("./pages/AbonnementAnnule"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Support = lazy(() => import("./pages/Support"));
const BugReport = lazy(() => import("./pages/BugReport"));
const AvisSupport = lazy(() => import("./pages/AvisSupport"));
const SuggestionsPage = lazy(() => import("./pages/Suggestions"));
const AvisPlateforme = lazy(() => import("./pages/AvisPlateforme"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
  </div>
);

const queryClient = new QueryClient();

const NotificationListener = () => {
  useNotifications();
  useInstantRenewal();
  useAppBadge();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <SiteSettingsProvider>
      <AuthProvider>
        <DesignModeProvider>
          <NotificationListener />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/messages" element={<ParentAccessGate><MessagesAccessRouter /></ParentAccessGate>} />
              <Route path="/search" element={<ParentAccessGate><Search /></ParentAccessGate>} />
              <Route path="/booking" element={<ParentAccessGate><Booking /></ParentAccessGate>} />
              <Route path="/pro/dashboard" element={<ProDashboard />} />
              <Route path="/pro/reservations" element={<ProReservations />} />
              <Route path="/demandes" element={<ParentAccessGate><CareRequests /></ParentAccessGate>} />
              <Route path="/favorites" element={<ParentAccessGate><Favorites /></ParentAccessGate>} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/seo" element={<AdminSeo />} />
              <Route path="/admin/social-agent" element={<AdminSocialAgent />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/mes-reservations" element={<ParentAccessGate><MyBookings /></ParentAccessGate>} />
              <Route path="/historique" element={<Historique />} />
              <Route path="/mes-gains" element={<MesGains />} />
              <Route path="/mon-profil" element={<MyProfile />} />
              <Route path="/profil/:userId" element={<PublicProfile />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/email-valide" element={<EmailVerified />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/contact/conversation" element={<ContactConversation />} />
              <Route path="/mentions-legales" element={<LegalNotice />} />
              <Route path="/confidentialite" element={<Privacy />} />
              <Route path="/cgu" element={<Terms />} />
              <Route path="/babysitter-france" element={<BabysitterFrance />} />
              <Route path="/baby-sitter/:ville" element={<CityLanding />} />
              <Route path="/baby-sitter" element={<CategoryHub />} />
              <Route path="/nounou" element={<CategoryHub />} />
              <Route path="/garde-enfants" element={<CategoryHub />} />
              <Route path="/assistante-maternelle" element={<CategoryHub />} />
              <Route path="/tarifs-baby-sitting" element={<TarifsBabySitting />} />
              <Route path="/comment-ca-marche-parents" element={<CommentCaMarcheParents />} />
              <Route path="/comment-ca-marche-pros" element={<CommentCaMarchePros />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/install" element={<Install />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/abonnement-succes" element={<AbonnementSucces />} />
              <Route path="/abonnement-annule" element={<AbonnementAnnule />} />
              <Route path="/support" element={<Support />} />
              <Route path="/signaler-un-bug" element={<BugReport />} />
              <Route path="/avis-support" element={<AvisSupport />} />
              <Route path="/suggestions" element={<SuggestionsPage />} />
              <Route path="/avis" element={<AvisPlateforme />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <DesignDomOverridesApplier />
            <DesignModeOverlays />
            <DesignContentSelector />
            <DesignModePanel />
            <DesignDomPanel />
            <LayersPanel />
            <DesignModeToolbar />
            <PwaUpdatePrompt />
            <PushPermissionPrompt />
            <SupportChatBubble />
            <ProAccountCompletionModal />
          </BrowserRouter>
        </DesignModeProvider>
      </AuthProvider>
      </SiteSettingsProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
